import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { empleadosService, Empleado, CreateEmpleadoDto, UpdateEmpleadoDto } from '../src/services/empleadosService';
import { empresasService, Empresa } from '../src/services/empresasService';
import { negociosService, Negocio } from '../src/services/negociosService';
import { useAuth } from '../src/contexts/AuthContext';

interface EmpleadosProps {
  onClose: () => void;
}

type SortableKey = 'nombreEmpleado' | 'especialidad' | 'calificacion' | 'disponible';

const Empleados: React.FC<EmpleadosProps> = ({ onClose }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.nombreRol === 'SuperAdmin';

  const [empleados, setEmpleados] = useState<Empleado[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [negocios, setNegocios] = useState<Negocio[]>([]);
  const [negociosFiltrados, setNegociosFiltrados] = useState<Negocio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingEmpleado, setEditingEmpleado] = useState<Empleado | null>(null);
  const [formData, setFormData] = useState<CreateEmpleadoDto>({
    empresaId: 0,
    negocioId: 0,
    nombreEmpleado: '',
    especialidad: null,
    calificacion: null,
    aniosExperiencia: null,
    imagenUrl: null,
    disponible: true,
    notas: null,
    creadoPor: null,
  });
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Default to user's empresa if not SuperAdmin
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | 'all'>(user?.empresaId ?? 'all');
  const [selectedNegocioId, setSelectedNegocioId] = useState<number | 'all'>('all');
  const [sortConfig, setSortConfig] = useState<{ key: SortableKey; direction: 'asc' | 'desc' } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes disminuir { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.001); opacity: 0; } }
      @keyframes crecer { 0% { transform: scale(0.001); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const isDarkMode = () => {
    const theme = localStorage.getItem('theme');
    if (theme) return theme === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const showAuthAlert = () => {
    const dark = isDarkMode();
    Swal.fire({
      icon: 'warning',
      title: 'Acceso Denegado',
      text: 'El usuario no está logueado',
      confirmButtonColor: '#f97316',
      background: dark ? '#1a1614' : '#ffffff',
      color: dark ? '#ffffff' : '#1a1614',
      customClass: {
        popup: 'rounded-2xl'
      }
    }).then(() => {
      onClose();
    });
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      showAuthAlert();
      return;
    }
    loadData();
  }, []);

  // Update selectedEmpresaId when user becomes available
  useEffect(() => {
    if (user?.empresaId && !isSuperAdmin) {
      setSelectedEmpresaId(user.empresaId);
    }
  }, [user?.empresaId, isSuperAdmin]);

  useEffect(() => {
    if (selectedEmpresaId !== 'all') {
      const filtered = negocios.filter(n => n.empresaId === selectedEmpresaId);
      setNegociosFiltrados(filtered);
      if (!filtered.find(n => n.negocioId === selectedNegocioId)) {
        setSelectedNegocioId('all');
      }
    } else {
      setNegociosFiltrados(negocios);
    }
  }, [selectedEmpresaId, negocios]);

  useEffect(() => {
    if (formData.empresaId > 0) {
      const filtered = negocios.filter(n => n.empresaId === formData.empresaId);
      setNegociosFiltrados(filtered);
    }
  }, [formData.empresaId, negocios]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [empleadosData, empresasData, negociosData] = await Promise.all([
        empleadosService.getAll(),
        empresasService.getAll(),
        negociosService.getAll()
      ]);
      setEmpleados(empleadosData);
      setEmpresas(empresasData);
      setNegocios(negociosData);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error al cargar datos';
      if (errorMsg.includes('401') || errorMsg.includes('Unauthorized')) {
        showAuthAlert();
        return;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmpleados = useMemo(() => {
    const lowered = searchTerm.toLowerCase();
    let filtered = empleados.filter(e =>
      e.nombreEmpleado.toLowerCase().includes(lowered) ||
      (e.especialidad && e.especialidad.toLowerCase().includes(lowered))
    );
    if (selectedEmpresaId !== 'all') {
      filtered = filtered.filter(e => e.empresaId === selectedEmpresaId);
    }
    if (selectedNegocioId !== 'all') {
      filtered = filtered.filter(e => e.negocioId === selectedNegocioId);
    }
    if (sortConfig) {
      filtered = [...filtered].sort((a, b) => {
        const dir = sortConfig.direction === 'asc' ? 1 : -1;
        if (sortConfig.key === 'disponible') {
          return (a.disponible === b.disponible ? 0 : a.disponible ? -1 : 1) * dir;
        }
        const aVal: any = (a as any)[sortConfig.key] ?? '';
        const bVal: any = (b as any)[sortConfig.key] ?? '';
        if (aVal < bVal) return -1 * dir;
        if (aVal > bVal) return 1 * dir;
        return 0;
      });
    }
    return filtered;
  }, [empleados, searchTerm, sortConfig, selectedEmpresaId, selectedNegocioId]);

  const handleSort = (key: SortableKey) => {
    setSortConfig(prev => {
      if (prev && prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const handleCreate = () => {
    setEditingEmpleado(null);
    // Default to selectedEmpresaId, or first active empresa for SuperAdmin
    const defaultEmpresaId = isSuperAdmin 
      ? (selectedEmpresaId !== 'all' ? selectedEmpresaId : (empresas.find(e => e.activa)?.empresaId ?? 0))
      : (user?.empresaId ?? 0);
    setFormData({
      empresaId: defaultEmpresaId,
      negocioId: selectedNegocioId === 'all' ? 0 : selectedNegocioId,
      nombreEmpleado: '',
      especialidad: null,
      calificacion: null,
      aniosExperiencia: null,
      imagenUrl: null,
      disponible: true,
      notas: null,
      creadoPor: localStorage.getItem('username') || null,
    });
    setSelectedFile(null);
    setImagePreview(null);
    setIsModalOpen(true);
  };

  const getFullImageUrl = (url: string | null) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const apiBase = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:7202/api').replace('/api', '');
    return `${apiBase}${url}`;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleEdit = (empleado: Empleado) => {
    setEditingEmpleado(empleado);
    setFormData({
      empresaId: empleado.empresaId,
      negocioId: empleado.negocioId,
      nombreEmpleado: empleado.nombreEmpleado,
      especialidad: empleado.especialidad,
      calificacion: empleado.calificacion,
      aniosExperiencia: empleado.aniosExperiencia,
      imagenUrl: empleado.imagenUrl,
      disponible: empleado.disponible,
      notas: empleado.notas,
      creadoPor: empleado.creadoPor,
    });
    setSelectedFile(null);
    setImagePreview(getFullImageUrl(empleado.imagenUrl));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setIsModalClosing(false);
      setEditingEmpleado(null);
    }, 300);
  };

  const handleDelete = async (id: number) => {
    const empleado = empleados.find(e => e.empleadoId === id);
    const nombre = empleado?.nombreEmpleado || `ID ${id}`;
    const result = await Swal.fire({
      title: '¿Eliminar empleado?',
      html: `Esta acción eliminará al empleado <strong>${nombre}</strong>.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      background: 'rgba(15,23,42,0.9)',
      color: '#fff',
      backdrop: false,
      customClass: {
        popup: 'rounded-2xl'
      }
    });
    if (!result.isConfirmed) return;
    try {
      await empleadosService.delete(id);
      setEmpleados(empleados.filter(e => e.empleadoId !== id));
      Swal.fire({ title: 'Empleado eliminado', icon: 'success', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#3b82f6', timer: 2000, timerProgressBar: true, backdrop: false, customClass: { popup: 'rounded-2xl' } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.empresaId === 0) {
      setError('Debe seleccionar una empresa');
      return;
    }

    if (formData.negocioId === 0) {
      setError('Debe seleccionar un negocio');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let savedEmpleado: Empleado;
      if (editingEmpleado) {
        const updateData: UpdateEmpleadoDto = { ...formData };
        savedEmpleado = await empleadosService.update(editingEmpleado.empleadoId, updateData);
      } else {
        savedEmpleado = await empleadosService.create(formData);
      }

      if (selectedFile) {
        const uploadResult = await empleadosService.uploadImage(savedEmpleado.empleadoId, selectedFile);
        savedEmpleado = { ...savedEmpleado, imagenUrl: uploadResult.imagenUrl };
      }

      if (editingEmpleado) {
        setEmpleados(empleados.map(e => e.empleadoId === savedEmpleado.empleadoId ? savedEmpleado : e));
        Swal.fire({ title: 'Empleado actualizado', icon: 'success', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#3b82f6', timer: 2000, timerProgressBar: true, backdrop: false, customClass: { popup: 'rounded-2xl' } });
      } else {
        setEmpleados([savedEmpleado, ...empleados]);
        Swal.fire({ title: 'Empleado creado', icon: 'success', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#3b82f6', timer: 2000, timerProgressBar: true, backdrop: false, customClass: { popup: 'rounded-2xl' } });
      }
      setEditingEmpleado(null);
      closeModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pt-4 fixed inset-0 z-50 bg-white dark:bg-background-dark overflow-auto" style={{ animation: 'crecer 0.3s ease-out' }}>
      <div className="dark:bg-background-dark text-gray-900 dark:text-white px-4 sm:px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="flex items-center gap-2 text-gray-900 dark:text-white/80 hover:text-gray-900 dark:hover:text-white transition-colors">
            <span className="material-symbols-outlined">arrow_back</span>
            <span className="hidden sm:inline">Volver</span>
          </button>
          <div>
            <h1 className="text-2xl text-secondary dark:text-white ">Empleados</h1>
            <p className="text-gray-900 dark:text-white text-sm hidden sm:block">Administra los empleados registrados</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="bg-[#307CAF] dark:bg-slate-800 text-white px-4 sm:px-6 py-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">search</span>
              <input type="text" placeholder="Buscar por nombre o especialidad..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-white/20 dark:border-slate-600 rounded-full bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-white/50 focus:border-transparent" />
            </div>
          </div>
          {isSuperAdmin && (
            <div className="sm:w-48">
              <select value={selectedEmpresaId} onChange={(e) => { setSelectedEmpresaId(e.target.value === 'all' ? 'all' : Number(e.target.value)); setSelectedNegocioId('all'); }}
                className="w-full px-3 py-2 border border-white/20 dark:border-slate-600 rounded-full bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-white/50 focus:border-transparent">
                <option value="all">Todas las empresas</option>
                {empresas.map((empresa) => (<option key={empresa.empresaId} value={empresa.empresaId}>{empresa.nombreEmpresa}</option>))}
              </select>
            </div>
          )}
          <div className="sm:w-48">
            <select value={selectedNegocioId} onChange={(e) => setSelectedNegocioId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="w-full px-3 py-2 border border-white/20 dark:border-slate-600 rounded-full bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-white/50 focus:border-transparent">
              <option value="all">Todos los negocios</option>
              {negociosFiltrados.map((negocio) => (<option key={negocio.negocioId} value={negocio.negocioId}>{negocio.nombreNegocio}</option>))}
            </select>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span>{filteredEmpleados.length} empleados</span>
            <button onClick={handleCreate} className="inline-flex items-center gap-1 px-4 py-2 bg-white text-[#307CAF] dark:bg-slate-600 dark:text-white rounded-full font-semibold hover:bg-white/90 dark:hover:bg-slate-500 transition">
              <span className="material-symbols-outlined text-base">add</span>Nuevo Empleado
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl text-red-600 dark:text-red-400 text-sm">{error}</div>
        )}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">
              <div className="animate-spin w-8 h-8 border-4 border-[#307CAF] border-t-transparent rounded-full mx-auto mb-4"></div>Cargando empleados...
            </div>
          ) : filteredEmpleados.length === 0 ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">
              <span className="material-symbols-outlined text-6xl text-gray-300 dark:text-gray-600 mb-4 block">assignment_ind</span>No se encontraron empleados con ese criterio.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-[#307CAF] text-white dark:bg-slate-700">
                  <tr>
                    <th onClick={() => handleSort('nombreEmpleado')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 dark:hover:bg-slate-600">
                      <div className="flex items-center gap-1">Nombre{sortConfig?.key === 'nombreEmpleado' && <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Negocio</th>
                    <th onClick={() => handleSort('especialidad')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 dark:hover:bg-slate-600">
                      <div className="flex items-center gap-1">Especialidad{sortConfig?.key === 'especialidad' && <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</div>
                    </th>
                    <th onClick={() => handleSort('calificacion')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 dark:hover:bg-slate-600">
                      <div className="flex items-center gap-1">Calificación{sortConfig?.key === 'calificacion' && <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</div>
                    </th>
                    <th onClick={() => handleSort('disponible')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 dark:hover:bg-slate-600">
                      <div className="flex items-center gap-1">Estado{sortConfig?.key === 'disponible' && <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {filteredEmpleados.map((empleado) => (
                    <tr key={empleado.empleadoId} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {empleado.imagenUrl ? (
                            <img src={getFullImageUrl(empleado.imagenUrl)!} alt={empleado.nombreEmpleado} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-[#307CAF]/10 flex items-center justify-center">
                              <span className="text-[#307CAF] font-bold text-sm">{empleado.nombreEmpleado.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}</span>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-slate-900 dark:text-white">{empleado.nombreEmpleado}</p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">{empleado.aniosExperiencia ? `${empleado.aniosExperiencia} años exp.` : ''}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-slate-600 dark:text-slate-300">{(empleado as any).nombreNegocio || negocios.find(n => n.negocioId === empleado.negocioId)?.nombreNegocio || `ID: ${empleado.negocioId}`}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{(empleado as any).nombreEmpresa || empresas.find(e => e.empresaId === empleado.empresaId)?.nombreEmpresa || ''}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">{empleado.especialidad || '-'}</td>
                      <td className="px-6 py-4">
                        {empleado.calificacion ? (
                          <div className="flex items-center gap-1">
                            <span className="material-symbols-outlined text-yellow-500 text-sm">star</span>
                            <span className="text-sm text-slate-700 dark:text-slate-300">{empleado.calificacion.toFixed(1)}</span>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${empleado.disponible ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300'}`}>
                          <span className="material-symbols-outlined text-sm">{empleado.disponible ? 'check_circle' : 'cancel'}</span>{empleado.disponible ? 'Disponible' : 'No disponible'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleEdit(empleado)} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 dark:border-slate-600 px-3 py-1 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700">
                            <span className="material-symbols-outlined text-sm">edit</span>Editar
                          </button>
                          <button onClick={() => handleDelete(empleado.empleadoId)} className="inline-flex items-center gap-1 rounded-lg border border-transparent px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                            <span className="material-symbols-outlined text-sm">delete</span>Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="relative w-full max-w-2xl rounded-2xl bg-white/95 dark:bg-slate-800 p-6 shadow-xl backdrop-blur-sm border border-gray-200 dark:border-slate-700 max-h-[90vh] overflow-y-auto" style={{ animation: isModalClosing ? 'disminuir 0.3s ease-out' : 'crecer 0.3s ease-out' }}>
            <button onClick={closeModal} className="absolute right-3 top-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white">
              <span className="material-symbols-outlined">close</span>
            </button>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">{editingEmpleado ? 'Editar Empleado' : 'Crear Empleado'}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{editingEmpleado ? 'Modifica los datos del empleado.' : 'Añade los datos del nuevo empleado.'}</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className={`grid gap-4 ${isSuperAdmin ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {isSuperAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Empresa *</label>
                    <select value={formData.empresaId} onChange={(e) => setFormData({ ...formData, empresaId: Number(e.target.value), negocioId: 0 })} required disabled={!!editingEmpleado}
                      className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]">
                      <option value={0}>Seleccionar empresa...</option>
                      {empresas.map((empresa) => (<option key={empresa.empresaId} value={empresa.empresaId}>{empresa.nombreEmpresa}</option>))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Negocio *</label>
                  <select value={formData.negocioId} onChange={(e) => setFormData({ ...formData, negocioId: Number(e.target.value) })} required disabled={!!editingEmpleado || formData.empresaId === 0}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF] disabled:opacity-50">
                    <option value={0}>Seleccionar negocio...</option>
                    {negociosFiltrados.map((negocio) => (<option key={negocio.negocioId} value={negocio.negocioId}>{negocio.nombreNegocio}</option>))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Nombre del Empleado *</label>
                <input type="text" value={formData.nombreEmpleado} onChange={(e) => setFormData({ ...formData, nombreEmpleado: e.target.value })} required maxLength={150}
                  className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Especialidad</label>
                  <input type="text" value={formData.especialidad || ''} onChange={(e) => setFormData({ ...formData, especialidad: e.target.value || null })} maxLength={100}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Calificación (0-5)</label>
                  <input type="number" step="0.1" min="0" max="5" value={formData.calificacion ?? ''} onChange={(e) => setFormData({ ...formData, calificacion: e.target.value ? parseFloat(e.target.value) : null })}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Años de Experiencia</label>
                  <input type="number" min="0" value={formData.aniosExperiencia ?? ''} onChange={(e) => setFormData({ ...formData, aniosExperiencia: e.target.value ? parseInt(e.target.value) : null })}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Imagen (opcional)</label>
                  <div className="flex items-center gap-4">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="h-16 w-16 rounded-full object-cover border-2 border-slate-200 dark:border-slate-700" />
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-[#307CAF]/10 flex items-center justify-center">
                        <span className="material-symbols-outlined text-[#307CAF] text-2xl">person</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border border-gray-200 dark:border-slate-600 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors">
                        <span className="material-symbols-outlined text-base">upload</span>
                        {selectedFile ? selectedFile.name : 'Seleccionar imagen'}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">JPG, PNG o WEBP. Máx 5 MB.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Notas</label>
                <textarea value={formData.notas || ''} onChange={(e) => setFormData({ ...formData, notas: e.target.value || null })} rows={3} maxLength={500}
                  className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-slate-600 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-[#307CAF] dark:text-slate-200">Disponibilidad</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Define si el empleado está disponible.</p>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <span className="mr-3 text-sm text-slate-500 dark:text-slate-400">{formData.disponible ? 'Disponible' : 'No disponible'}</span>
                  <input type="checkbox" className="sr-only" checked={formData.disponible} onChange={(e) => setFormData({ ...formData, disponible: e.target.checked })} />
                  <span className={`relative inline-block h-6 w-11 rounded-full transition ${formData.disponible ? 'bg-[#307CAF]' : 'bg-slate-400'}`}>
                    <span className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition ${formData.disponible ? 'translate-x-5' : ''}`} />
                  </span>
                </label>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" className="rounded-lg border border-gray-200 dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={closeModal}>Cancelar</button>
                <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-[#307CAF] px-4 py-2 text-sm font-semibold text-white shadow hover:bg-[#307CAF]/90 disabled:opacity-60">
                  {saving ? (<><span className="material-symbols-outlined animate-spin">progress_activity</span>Guardando...</>) : (<><span className="material-symbols-outlined">save</span>Guardar</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Empleados;
